import React, { useRef } from "react";
import { useOnClickOutside } from "utilities/hookutils";
import { SaveOrCancelButtons } from "./SaveOrCancelButtons";
import { RenderEditOptions } from "./types";

interface Props<T> {
  value: T
  render: () => React.ReactNode
  renderEdit: (opt: RenderEditOptions<T>) => React.ReactNode
  onValueUpdated: (newVal: T) => void
}

// This is a variant of in page editing that can be used standalone, without a form context and submit system.
// It is much simpler, and designed for use primarily for text fields.
export const InPageEditableStandaloneField = <T extends any>(props: Props<T>) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [value, setValue] = React.useState<T>(props.value);
  const inPageEditRef = useRef<HTMLDivElement>(null);

  const save = (v: T) => {
    props.onValueUpdated(v);
    setIsEditing(false);
  }

  const cancel = () => {
    setValue(props.value);
    setIsEditing(false);
  }

  const onChange = (newVal: T, submit?: boolean) => {
    if (submit) {
      save(newVal);
    } else {
      setValue(newVal);
    }
  }

  useOnClickOutside(inPageEditRef, () => {
    save(value);
  });

  return <div className="in-page-editable-standalone-field-wrapper">
    {isEditing && <div ref={inPageEditRef}>
      {props.renderEdit({
        value,
        onChange,
        cancel,
        submit: () => save(value),
        error: false,
        isSaving: false
      })}
      <SaveOrCancelButtons
        onSave={() => save(value)}
        onCancel={cancel} />
    </div>}
    {!isEditing && <div className="in-page-editable-standalone-field" onClick={() => setIsEditing(true)}>
      {props.render()}
    </div>}
  </div>
}